import { createClient, type Session } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL as string;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!url || !anonKey) {
  throw new Error('VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are required');
}

export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'cartrack-supabase-session',
  },
});

export const RECEIPTS_BUCKET = 'service-receipts';
export const VEHICLE_PHOTOS_BUCKET = 'vehicle-photos';

/**
 * Upload a file to a private, owner-scoped Supabase Storage bucket. Path scheme
 * is `{userId}/{vehicleId}/{timestamp}-{sanitizedName}` — the top-level userId
 * folder is what the owner-folder RLS policy keys off of. Returns the storage
 * path on success (this is what is persisted in Postgres). Shared by receipts
 * and vehicle photos so both use the identical, RLS-compatible path layout.
 */
async function uploadOwnerScoped(
  bucket: string,
  userId: string,
  vehicleId: string,
  file: File,
): Promise<string> {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, '_').slice(-60);
  const path = `${userId}/${vehicleId}/${Date.now()}-${safeName}`;
  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type || 'application/octet-stream',
  });
  if (error) throw error;
  return path;
}

/** Get a short-lived signed URL for viewing a private stored object. */
async function getSignedUrl(
  bucket: string,
  path: string,
  expiresIn = 3600,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn);
  if (error) throw error;
  return data.signedUrl;
}

/**
 * Upload a receipt file. Returns the storage path (ServiceLog.receiptUrl).
 */
export function uploadReceipt(userId: string, vehicleId: string, file: File): Promise<string> {
  return uploadOwnerScoped(RECEIPTS_BUCKET, userId, vehicleId, file);
}

/** Get a short-lived signed URL for viewing a stored receipt. */
export function getReceiptSignedUrl(path: string, expiresIn = 3600): Promise<string> {
  return getSignedUrl(RECEIPTS_BUCKET, path, expiresIn);
}

/**
 * Upload a vehicle photo. Returns the storage path (Vehicle.photoUrl). Mirrors
 * uploadReceipt exactly (same path scheme + sanitization + private upload).
 */
export function uploadVehiclePhoto(
  userId: string,
  vehicleId: string,
  file: File,
): Promise<string> {
  return uploadOwnerScoped(VEHICLE_PHOTOS_BUCKET, userId, vehicleId, file);
}

/** Get a short-lived signed URL for displaying a stored vehicle photo. */
export function getVehiclePhotoSignedUrl(path: string, expiresIn = 3600): Promise<string> {
  return getSignedUrl(VEHICLE_PHOTOS_BUCKET, path, expiresIn);
}

export type { Session };
